import type { LifeSpace, StartPeriod, TaskChanges } from '@/src/renderer/lib/db/models'
import { wakeStart, type WakeSource } from '@/src/renderer/lib/recurrence'

/**
 * One discrete decision the user makes about a task — either inside the
 * Clarify-Mode (Kurz notiert) or via the dormant Reactivate dropdown.
 *
 * `wakeInProject` is Reactivate-only and unpauses the task without changing
 * its dates / project assignment — used when a paused task already has a
 * project and the user just wants to wake it up there again.
 */
export type StagedAction =
  | { kind: 'today' }
  | { kind: 'tomorrow' }
  | { kind: 'later'; date: Date; time: string | null; period: StartPeriod | null }
  | { kind: 'pause' }
  /**
   * `place` names the target for card and toast: „Garten“ or „Garten / Beete“ (#267). The move
   * panel offers every life space, so the life space travels along — without it the task would
   * sit in a foreign project but stay in its old life space. `icon`/`iconColor` are the project's
   * icon for the Clarify card („Nach [Icon] Garten“).
   */
  | { kind: 'move'; projectId: string; sectionId: string | null; lifeSpace: LifeSpace; place: string; icon?: string | null; iconColor?: string | null }
  /**
   * Clarify-only (#267): „Neues Projekt „…““ from the move panel. The project is created when the
   * batch is committed (K), not when it is picked — leaving Clarify with Esc must not leave an
   * empty project behind. `commitClarify` turns it into a `move`.
   */
  | { kind: 'newProject'; name: string; lifeSpace: LifeSpace }
  | { kind: 'delete' }
  /**
   * Reactivate-only: clears `pausedAt` without touching dates or
   * `projectId`. Carries `projectId` + `projectName` + `lifeSpace` so
   * the toast can offer an "open project" button.
   */
  | { kind: 'wakeInProject'; projectId: string; projectName: string; lifeSpace: 'personal' | 'work' | 'home' }

/**
 * Project a `StagedAction` into the `Partial<Task>` payload that gets
 * written to the DB. Single source of truth for both Clarify (batch
 * commit) and Reactivate (single-action commit).
 *
 * `task` is only read by `wakeInProject`: a recurring task that slept past
 * its date wakes on its next date (see {@link wakeTaskUpdate}).
 *
 * **Löschen ist kein Update** (#224): Die Aufrufer zweigen `delete` vorher ab und nehmen
 * `deleteTask` — nur er nimmt die Kaskade mit und meldet die Löschung der Cloud.
 */
export function buildTaskUpdate(
  action: Exclude<StagedAction, { kind: 'delete' | 'newProject' }>,
  now: Date,
  task?: WakeSource,
): TaskChanges {
  switch (action.kind) {
    case 'today':
      return {
        startDate: atMidnight(now),
        startTime: null,
        startPeriod: null,
        pausedAt: null,
      }
    case 'tomorrow': {
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      return {
        startDate: atMidnight(tomorrow),
        startTime: null,
        startPeriod: null,
        pausedAt: null,
      }
    }
    case 'later':
      return {
        startDate: atMidnight(action.date),
        startTime: action.time,
        startPeriod: action.period,
        pausedAt: null,
      }
    case 'pause':
      return { pausedAt: now }
    case 'move':
      return {
        projectId: action.projectId,
        sectionId: action.sectionId,
        lifeSpace: action.lifeSpace,
        pausedAt: null,
      }
    case 'wakeInProject':
      return task ? wakeTaskUpdate(task, now) : { pausedAt: null }
  }
}

/**
 * Waking a task WITHOUT picking a date — the direct wake in a project, the
 * rest shortcut, "Im Projekt" in the Reactivate menu. Every such path goes
 * through here: **Aufwecken holt den Termin nach vorn** (CONTEXT.md,
 * Wiederholung) — a recurring task that slept past its date wakes on its
 * next date instead of overdue.
 */
export function wakeTaskUpdate(task: WakeSource, now: Date): TaskChanges {
  return { pausedAt: null, ...wakeStart(task, now) }
}

function atMidnight(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}
